import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, apiDelete, apiPatch } from "../lib/api";
import { useAuth } from "../context/AuthProvider";
import "./AdminSavKabile.css";

interface DailyQuestion {
  _id: string;
  question: string;
  category: string;
  scheduledTime: string; // HH:MM format
  isActive: boolean;
  createdAt: string;
  usedDate?: string;
}

interface QuestionStats {
  totalQuestions: number;
  usedQuestions: number;
  activeQuestions: number;
  todaysQuestion?: {
    question: string;
    askedAt: string;
    responses: number;
  };
}

interface CronConfig {
  enabled: boolean;
  hour: number;
  minute: number;
}

export default function AdminSavKabile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<QuestionStats | null>(null);
  const [questions, setQuestions] = useState<DailyQuestion[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<DailyQuestion[]>([]);
  
  // Cron states
  const [cronConfig, setCronConfig] = useState<CronConfig>({ enabled: true, hour: 21, minute: 0 });
  const [cronLoading, setCronLoading] = useState(false);
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newCategory, setNewCategory] = useState("siyaset");
  const [scheduledTime, setScheduledTime] = useState("21:00");
  
  // Filter states
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Edit states
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editTime, setEditTime] = useState("");

  const categories = [
    "siyaset",
    "felsefe",
    "toplum",
    "psikoloji",
    "değerler",
    "ekonomi",
    "kültür",
    "tarih",
    "etik",
    "güncel"
  ];

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate("/");
      return;
    }
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [questions, filterCategory, filterStatus, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, questionsRes, cronRes] = await Promise.all([
        apiGet<{ ok: true; stats: QuestionStats }>("/api/admin/savkabile/stats"),
        apiGet<{ ok: true; questions: DailyQuestion[] }>("/api/admin/savkabile/questions"),
        apiGet<{ ok: true; config: CronConfig }>("/api/admin/savkabile/cron/config")
      ]);
      setStats(statsRes.stats);
      setQuestions(questionsRes.questions);
      setCronConfig(cronRes.config);
    } catch (error: any) {
      console.error("Failed to load data:", error);
      alert(error.message || "Veri yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...questions];

    // Category filter
    if (filterCategory !== "all") {
      filtered = filtered.filter(q => q.category === filterCategory);
    }

    // Status filter
    if (filterStatus === "active") {
      filtered = filtered.filter(q => q.isActive && !q.usedDate);
    } else if (filterStatus === "used") {
      filtered = filtered.filter(q => q.usedDate);
    } else if (filterStatus === "inactive") {
      filtered = filtered.filter(q => !q.isActive);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(q => 
        q.question.toLowerCase().includes(query) ||
        q.category.toLowerCase().includes(query)
      );
    }

    setFilteredQuestions(filtered);
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) {
      alert("Lütfen soru metni girin");
      return;
    }

    try {
      await apiPost("/api/admin/savkabile/questions", {
        question: newQuestion.trim(),
        category: newCategory,
        scheduledTime
      });
      
      setNewQuestion("");
      setNewCategory("siyaset");
      setScheduledTime("21:00");
      setShowAddForm(false);
      
      await loadData();
      alert("Soru başarıyla eklendi");
    } catch (error: any) {
      console.error("Failed to add question:", error);
      alert(error.message || "Soru eklenirken hata oluştu");
    }
  };

  const handleEditQuestion = async (id: string) => {
    if (!editQuestion.trim()) {
      alert("Lütfen soru metni girin");
      return;
    }

    try {
      await apiPatch(`/api/admin/savkabile/questions/${id}`, {
        question: editQuestion.trim(),
        category: editCategory,
        scheduledTime: editTime
      });
      
      setEditingId(null);
      setEditQuestion("");
      setEditCategory("");
      setEditTime("");
      
      await loadData();
      alert("Soru güncellendi");
    } catch (error: any) {
      console.error("Failed to update question:", error);
      alert(error.message || "Soru güncellenirken hata oluştu");
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await apiPatch(`/api/admin/savkabile/questions/${id}/toggle`, {
        isActive: !currentStatus
      });
      await loadData();
    } catch (error: any) {
      console.error("Failed to toggle status:", error);
      alert(error.message || "Durum değiştirilirken hata oluştu");
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    if (!confirm("Bu soruyu silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await apiDelete(`/api/admin/savkabile/questions/${id}`);
      await loadData();
      alert("Soru silindi");
    } catch (error: any) {
      console.error("Failed to delete question:", error);
      alert(error.message || "Soru silinirken hata oluştu");
    }
  };

  const startEdit = (question: DailyQuestion) => {
    setEditingId(question._id);
    setEditQuestion(question.question);
    setEditCategory(question.category);
    setEditTime(question.scheduledTime);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditQuestion("");
    setEditCategory("");
    setEditTime("");
  };

  const handleManualStart = async () => {
    if (!confirm("Günlük savaşı manuel olarak başlatmak istediğinizden emin misiniz?\n\nBu işlem mevcut aktif savaşı kapatıp yeni bir soru seçecek.")) {
      return;
    }

    try {
      setCronLoading(true);
      await apiPost("/api/admin/savkabile/battle/start", {});
      alert("Günlük savaş başarıyla başlatıldı! ✅");
      await loadData();
    } catch (error: any) {
      console.error("Failed to start battle:", error);
      alert(error.message || "Savaş başlatılamadı");
    } finally {
      setCronLoading(false);
    }
  };

  const handleCronConfigUpdate = async () => {
    try {
      setCronLoading(true);
      await apiPost("/api/admin/savkabile/cron/config", cronConfig);
      alert("Cron ayarları güncellendi! ✅");
    } catch (error: any) {
      console.error("Failed to update cron config:", error);
      alert(error.message || "Cron ayarları güncellenemedi");
    } finally {
      setCronLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-savkabile-container">
        <div className="loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="admin-savkabile-container">
      <div className="admin-savkabile-content">
        {/* Header */}
        <header className="admin-header">
          <button onClick={() => navigate("/settings")} className="back-button">
            ← Ayarlar
          </button>
          <div className="header-title">
            <h1>🏛️ SavKabile Yönetimi</h1>
            <p>Günlük soru sistemi ve kabile savaşları yönetimi</p>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card stat-card-total">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-value">{stats?.totalQuestions || 0}</div>
              <div className="stat-label">Toplam Soru</div>
            </div>
          </div>

          <div className="stat-card stat-card-active">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-value">{stats?.activeQuestions || 0}</div>
              <div className="stat-label">Aktif Soru</div>
            </div>
          </div>

          <div className="stat-card stat-card-used">
            <div className="stat-icon">✔️</div>
            <div className="stat-info">
              <div className="stat-value">{stats?.usedQuestions || 0}</div>
              <div className="stat-label">Kullanılan Soru</div>
            </div>
          </div>

          <div className="stat-card stat-card-today">
            <div className="stat-icon">🎯</div>
            <div className="stat-info">
              <div className="stat-value">
                {stats?.todaysQuestion ? "Aktif" : "Yok"}
              </div>
              <div className="stat-label">Bugünün Sorusu</div>
            </div>
          </div>
        </div>

        {/* Today's Question */}
        {stats?.todaysQuestion && (
          <div className="todays-question-card">
            <h3>📌 Bugünün Aktif Sorusu</h3>
            <p className="question-text">{stats.todaysQuestion.question}</p>
            <div className="question-meta">
              <span>⏰ Sorulma: {new Date(stats.todaysQuestion.askedAt).toLocaleString("tr-TR")}</span>
              <span>💬 Cevap: {stats.todaysQuestion.responses}</span>
            </div>
          </div>
        )}

        {/* Cron Configuration */}
        <div className="cron-config-card">
          <h3>🕐 Otomatik Soru Sistemi</h3>
          
          <div className="cron-settings">
            <div className="cron-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={cronConfig.enabled}
                  onChange={(e) => setCronConfig({ ...cronConfig, enabled: e.target.checked })}
                />
                <span>Otomatik soru sistemi aktif</span>
              </label>
            </div>

            <div className="cron-time-picker">
              <label>Günlük Soru Saati:</label>
              <div className="time-inputs">
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={cronConfig.hour}
                  onChange={(e) => setCronConfig({ ...cronConfig, hour: parseInt(e.target.value) || 0 })}
                  className="time-input"
                />
                <span>:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={cronConfig.minute}
                  onChange={(e) => setCronConfig({ ...cronConfig, minute: parseInt(e.target.value) || 0 })}
                  className="time-input"
                />
              </div>
              <small>Her gün saat {String(cronConfig.hour).padStart(2, '0')}:{String(cronConfig.minute).padStart(2, '0')}'de otomatik soru başlatılacak</small>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#4caf50' }}>
                🌍 Saat Dilimi: <strong>Türkiye Saati (UTC+3)</strong>
              </small>
            </div>

            <div className="cron-actions">
              <button
                onClick={handleCronConfigUpdate}
                disabled={cronLoading}
                className="cron-save-btn"
              >
                💾 Ayarları Kaydet
              </button>

              <button
                onClick={handleManualStart}
                disabled={cronLoading}
                className="cron-manual-btn"
              >
                🚀 Şimdi Savaş Başlat (Manuel)
              </button>
            </div>
          </div>

          <div className="cron-info">
            <p>ℹ️ <strong>Otomatik Sistem:</strong> Her gün belirlediğiniz saatte rastgele bir soru seçilip yeni savaş başlatılır.</p>
            <p>⚡ <strong>Manuel Başlatma:</strong> Test veya acil durumlarda hemen yeni savaş başlatabilirsiniz.</p>
          </div>
        </div>

        {/* System Info */}
        <div className="system-info-card">
          <h3>⚙️ Sistem Bilgisi</h3>
          <ul>
            <li><strong>Soru Sorma Saati:</strong> Her gün saat {String(cronConfig.hour).padStart(2, '0')}:{String(cronConfig.minute).padStart(2, '0')} (Türkiye Saati - UTC+3)</li>
            <li><strong>Cevap Süresi:</strong> 24 saat (Bir sonraki günün aynı saatine kadar)</li>
            <li><strong>Oy Sistemi:</strong> TP'ye göre ağırlıklı (0-99: 1 oy, 100-499: 2 oy, 500-999: 3 oy, 1000+: 4 oy)</li>
            <li><strong>Lider Seçimi:</strong> Kabile lideri top 3 cevaptan birini seçer</li>
            <li><strong>Global Savaş:</strong> Tüm kabileler KP için yarışır</li>
          </ul>
        </div>

        {/* Add Question Button */}
        <div className="action-section">
          <button 
            onClick={() => setShowAddForm(!showAddForm)} 
            className="add-question-btn"
          >
            {showAddForm ? "❌ İptal" : "➕ Yeni Soru Ekle"}
          </button>
        </div>

        {/* Add Question Form */}
        {showAddForm && (
          <div className="question-form-card">
            <h3>📝 Yeni Soru Ekle</h3>
            
            <div className="form-group">
              <label>Soru Metni</label>
              <textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Örn: Sence bir ülkede gerçek adalet nasıl sağlanır?"
                rows={4}
                className="form-textarea"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kategori</label>
                <select 
                  value={newCategory} 
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="form-select"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Sorulma Saati (varsayılan)</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <button onClick={handleAddQuestion} className="submit-btn">
              ✅ Soruyu Ekle
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <div className="filter-group">
            <label>Kategori</label>
            <select 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tümü</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Durum</label>
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="used">Kullanılmış</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Ara</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Soru metni veya kategori ara..."
              className="search-input"
            />
          </div>
        </div>

        {/* Questions List */}
        <div className="questions-section">
          <h3>📋 Soru Havuzu ({filteredQuestions.length})</h3>
          
          {filteredQuestions.length === 0 ? (
            <div className="no-questions">
              <p>Filtreye uygun soru bulunamadı</p>
            </div>
          ) : (
            <div className="questions-list">
              {filteredQuestions.map(q => (
                <div key={q._id} className={`question-item ${q.usedDate ? 'used' : ''} ${!q.isActive ? 'inactive' : ''}`}>
                  {editingId === q._id ? (
                    // Edit mode
                    <div className="question-edit-form">
                      <textarea
                        value={editQuestion}
                        onChange={(e) => setEditQuestion(e.target.value)}
                        rows={3}
                        className="form-textarea"
                      />
                      <div className="form-row">
                        <select 
                          value={editCategory} 
                          onChange={(e) => setEditCategory(e.target.value)}
                          className="form-select"
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                        <input
                          type="time"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                          className="form-input"
                        />
                      </div>
                      <div className="edit-actions">
                        <button onClick={() => handleEditQuestion(q._id)} className="save-btn">
                          ✅ Kaydet
                        </button>
                        <button onClick={cancelEdit} className="cancel-btn">
                          ❌ İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <>
                      <div className="question-header">
                        <span className={`category-badge ${q.category}`}>
                          {q.category}
                        </span>
                        <span className="question-time">⏰ {q.scheduledTime}</span>
                        {q.usedDate && (
                          <span className="used-badge">
                            ✔️ {new Date(q.usedDate).toLocaleDateString("tr-TR")}
                          </span>
                        )}
                        {!q.isActive && (
                          <span className="inactive-badge">⏸️ Pasif</span>
                        )}
                      </div>

                      <p className="question-text">{q.question}</p>

                      <div className="question-actions">
                        <button 
                          onClick={() => startEdit(q)} 
                          className="edit-btn"
                          disabled={!!q.usedDate}
                        >
                          ✏️ Düzenle
                        </button>
                        <button 
                          onClick={() => handleToggleActive(q._id, q.isActive)}
                          className={q.isActive ? "deactivate-btn" : "activate-btn"}
                          disabled={!!q.usedDate}
                        >
                          {q.isActive ? "⏸️ Pasif Yap" : "▶️ Aktif Yap"}
                        </button>
                        <button 
                          onClick={() => handleDeleteQuestion(q._id)} 
                          className="delete-btn"
                          disabled={!!q.usedDate}
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
